import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import MapGL, { MapRef, Marker, NavigationControl, Popup } from "react-map-gl/mapbox";
import { MAPBOX_TOKEN } from "../../../config";
import { useDoctorsData } from "../../../providers/doctorsData";
import { ILocation } from "../../../providers/geolocation/types/intext";
import { geocodeAddress } from "../../../utils/geocode";

// O Babel do CRA transpila o mapbox-gl no build de produção e corrompe o
// web worker (erros "o is not defined"/"composite" e mapa cinza sem tiles).
// Fix oficial do Mapbox p/ CRA: carregar o worker CSP sem transpilação.
// https://docs.mapbox.com/mapbox-gl-js/guides/install/#transpiling
// eslint-disable-next-line import/no-webpack-loader-syntax
(mapboxgl as any).workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

interface MapViewProps {
  location?: ILocation;
  style?: React.CSSProperties;
}

export interface MapViewHandle {
  resize: () => void;
}

interface IPlaceMarker {
  id: number;
  name: string;
  address: string;
  geoQuery: string;
  doctors: string[];
  lat: number;
  lng: number;
}

// Curitiba/PR — centro padrão enquanto não há locais nem geolocalização
const DEFAULT_CENTER = { lat: -25.4284, lng: -49.2733 };

const MapView = forwardRef<MapViewHandle, MapViewProps>((props, ref) => {
  const { doctors } = useDoctorsData();

  const { location = {} as ILocation, style = { width: 200, height: 300 } } = props;

  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [markers, setMarkers] = useState<IPlaceMarker[]>([]);
  const [geocoding, setGeocoding] = useState(false);
  const [selected, setSelected] = useState<IPlaceMarker | null>(null);

  // agrupa os locais de atendimento dos profissionais listados (um pin por local)
  const places = useMemo(() => {
    const byId = new Map<number, Omit<IPlaceMarker, "lat" | "lng">>();

    doctors.forEach((doctor) => {
      doctor.places?.forEach((place) => {
        const street = place.addressName
          ? `${place.addressName}${place.number ? `, ${place.number}` : ""}`
          : "";
        const address = [street, place.neighborhood, place.city && `${place.city} - ${place.state}`]
          .filter(Boolean)
          .join(", ");

        if (!address || !place.city) return;

        const existing = byId.get(place.id);
        if (existing) {
          if (!existing.doctors.includes(doctor.name)) existing.doctors.push(doctor.name);
        } else {
          byId.set(place.id, {
            id: place.id,
            name: place.name,
            address,
            geoQuery: [street, place.neighborhood, place.city, place.state, place.cep]
              .filter(Boolean)
              .join(", "),
            doctors: [doctor.name],
          });
        }
      });
    });

    return Array.from(byId.values());
  }, [doctors]);

  // geocodifica os endereços (com cache) e posiciona os pins
  useEffect(() => {
    let active = true;

    if (places.length === 0) {
      setMarkers([]);
      setGeocoding(false);
      return;
    }

    setGeocoding(true);
    (async () => {
      const results = await Promise.all(
        places.map(async (place) => {
          const coords = await geocodeAddress(place.geoQuery);
          return coords ? { ...place, ...coords } : null;
        })
      );

      if (active) {
        setMarkers(results.filter(Boolean) as IPlaceMarker[]);
        setGeocoding(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [places]);

  const forceResize = () => {
    if (mapRef.current) {
      mapRef.current.resize();

      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.resize();
        }
      }, 50);
    }
  };

  useImperativeHandle(ref, () => ({
    resize: forceResize,
  }));

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        forceResize();
      }, 100);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      forceResize();
    }, 100);
  }, [style]);

  // enquadra todos os pins (e a posição do usuário, se houver) no mapa
  const fitToMarkers = () => {
    if (!mapRef.current) return;

    const points = markers.map((m) => ({ lat: m.lat, lng: m.lng }));
    if (location.lat && location.lng) points.push({ lat: location.lat, lng: location.lng });
    if (points.length === 0) return;

    const lngs = points.map((p) => p.lng);
    const lats = points.map((p) => p.lat);

    mapRef.current.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: 64, maxZoom: 14, duration: 800 }
    );
  };

  useEffect(() => {
    fitToMarkers();
    setSelected(null);
  }, [markers]);

  const handleMapLoad = () => {
    setTimeout(() => {
      forceResize();
      fitToMarkers();
    }, 100);
  };

  const initialCenter =
    location.lat && location.lng
      ? { lat: location.lat, lng: location.lng }
      : markers.length > 0
        ? { lat: markers[0].lat, lng: markers[0].lng }
        : DEFAULT_CENTER;

  if (!MAPBOX_TOKEN)
    return (
      <div className="w-100 p-4 text-center">
        <p className="opacity-50 mb-0">
          Mapa indisponível: configure a variável REACT_APP_MAPBOX_TOKEN.
        </p>
      </div>
    );

  return (
    <div ref={containerRef} style={style} className="position-relative overflow-hidden">
      {geocoding && (
        <div className="map-status-chip" role="status">
          <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
          Localizando endereços...
        </div>
      )}

      {!geocoding && markers.length === 0 && (
        <div className="map-status-chip" role="status">
          <i className="las la-map-marker-alt me-2" aria-hidden="true"></i>
          Nenhum local de atendimento para exibir
        </div>
      )}

      <MapGL
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{
          longitude: initialCenter.lng,
          latitude: initialCenter.lat,
          zoom: 12,
        }}
        style={{ width: "100%", height: "100%", borderRadius: "var(--bs-border-radius)" }}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        onLoad={handleMapLoad}
        onClick={() => setSelected(null)}
      >
        <NavigationControl position="bottom-right" showCompass={false} />

        {location.lat && location.lng && (
          <Marker longitude={location.lng} latitude={location.lat}>
            <span className="map-user-dot" title="Você está aqui" aria-label="Sua localização" />
          </Marker>
        )}

        {markers.map((marker) => (
          <Marker key={marker.id} longitude={marker.lng} latitude={marker.lat} anchor="bottom">
            <button
              type="button"
              className={`map-pin ${selected?.id === marker.id ? "active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setSelected(marker);
              }}
              aria-label={`${marker.name}: ${marker.doctors.length} ${
                marker.doctors.length === 1 ? "profissional" : "profissionais"
              }`}
            >
              <i className="las la-clinic-medical" aria-hidden="true"></i>
              {marker.doctors.length > 1 && (
                <span className="map-pin-badge">{marker.doctors.length}</span>
              )}
            </button>
          </Marker>
        ))}

        {selected && (
          <Popup
            longitude={selected.lng}
            latitude={selected.lat}
            anchor="bottom"
            offset={44}
            maxWidth="280px"
            focusAfterOpen={false}
            closeButton={false}
            onClose={() => setSelected(null)}
          >
            <div className="map-popup">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <strong className="map-popup-title">{selected.name}</strong>
                <button
                  type="button"
                  className="btn-close map-popup-close"
                  aria-label="Fechar detalhes do local"
                  onClick={() => setSelected(null)}
                ></button>
              </div>

              <p className="map-popup-address">
                <i className="las la-map-marker-alt me-1" aria-hidden="true"></i>
                {selected.address}
              </p>

              <ul className="map-popup-doctors">
                {selected.doctors.slice(0, 3).map((name) => (
                  <li key={name}>
                    <i className="las la-user-md me-1" aria-hidden="true"></i>
                    {name}
                  </li>
                ))}
                {selected.doctors.length > 3 && (
                  <li className="text-muted">+ {selected.doctors.length - 3} outros profissionais</li>
                )}
              </ul>
            </div>
          </Popup>
        )}
      </MapGL>
    </div>
  );
});

MapView.displayName = "MapView";

export default MapView;
